import React from 'react';
import { Field, change, getFormValues, isDirty } from 'redux-form';
import { ReduxControls, ErrorBanner } from '@flashtalking/rhombus-react';
import { useDispatch, useSelector } from 'react-redux';

import { adSizes } from './CreateDisplayConceptModal';
import CustomSizes from "widgets/modals/creatives/CustomSizes";
import { syncErrorsSelector } from 'widgets/forms/validators/CreateErrorMessages';

import { addNewAdstudioCreativesToConcept } from 'reducers/ConceptDetails';
import { closeModal } from 'reducers/Modal';
import { loadCreatives } from 'reducers/Creatives';
import { noop } from "utils/common";

import './SelectCreativeTypeNew.css';

export default function SelectAdsizesForConcept(props) {
	const { handleSubmit, libraryId, conceptId, initialValues, formErrors, setIsLoading=noop, pristine, formName } = props
		, dispatch = useDispatch()
		, isAllSelected = useSelector((state) => {
			let baseSizes = getFormValues(formName)(state)?.['base-sizes'] || {};
			let filtered = Object.values(baseSizes).filter(value => value);
			return filtered.length === adSizes.length ;
		});

	const {
		errorMessages,
		conceptName,
		formValues,
		isEdited,
	} = useSelector(state => ({
		errorMessages: syncErrorsSelector(formName)(state),
		conceptName: state.conceptDetails.data.conceptName,
		formValues: getFormValues(formName)(state),
		isEdited: isDirty(formName)(state),
	}));

	const customSizes = formValues?.['custom-sizes'];

	const filterInitialSizes = (filterValue, initialValKey) => {
		if(initialValues['custom-sizes'].some(size => size.width === filterValue.width && size.height === filterValue.height)) {
			return true;
		}
		return Object.keys(initialValues[initialValKey]).includes(filterValue);
	};

	let selectAllDisabled = false;
	if(Object.keys(formValues['base-sizes']).length === adSizes.length && pristine){
		selectAllDisabled = true;
	}

	const handleSelectAll = () => {
		if(isAllSelected){
			let filteredSizes = Object.keys(formValues['base-sizes'])

				// filter intial selected sizes
				.filter(key => !filterInitialSizes(key, 'base-sizes'))

				// return obj with filtered sizes
				.reduce((obj, key) => {
					obj[key] = formValues['base-sizes'][key];
					return obj;
				}, {});

			// if all true, set to false minus filteredSizes
			for(const key in filteredSizes) {
				dispatch(change(formName, `base-sizes.${key}`, false));
			}

		// set all true
		} else {
			adSizes.forEach(fld=>dispatch(change(formName, `base-sizes[${fld}]`, true)));
		}
	};

	function renderHeader() {
		if(formValues?.['custom-sizes'] && errorMessages.length) {
			let msgSet = new Set(errorMessages);
			return <ErrorBanner title="Sorry, please fix the errors below and try again." messages={[...msgSet]} />;
		} else {
			return <strong>Select Ad Studio placement sizes</strong>;
		}
	}
	const makeConcept = handleSubmit((values) => {
		setIsLoading(true);
		dispatch(addNewAdstudioCreativesToConcept(libraryId, conceptId, buildConceptSizes(values, conceptId)))
			.then((resp) => {
				setIsLoading(false);
				if(resp.success){
					dispatch(closeModal());
					dispatch(loadCreatives(libraryId));
				}
			});
	});

	const renderCheckboxes = () => {
		return adSizes.map((value) => {
			const name = value.replace('base-sizes', '');

			let currentVal = formValues["base-sizes"][value]
				// this is undefined on first run something to do with the  initialValues useMemo?
				, initialVal = initialValues['base-sizes'][value]
				, disabled = false;

			if(!isEdited && currentVal === true) {
				disabled = true;
			} else if(!!initialVal) {
				disabled = true;
			}

			return (
				<Field
					component={ReduxControls.Checkbox}
					type="checkbox"
					name={`base-sizes.${value}`}
					label={name}
					key={`checkbox-${name}`}
					disabled={disabled}
				 />
			);
		});
	};

	const buildConceptSizes = (values, libraryId) => {
		let conceptData =  {
			conceptType: "HTML",
			creativeLibraryId: libraryId,
			conceptName,
			sizes: [],
		};

		if(values){
			Object.entries(values['base-sizes']).forEach((baseSize) => {
				let size = baseSize[0].replace('base-sizes.', '');
				// remove existing sizes and only push true vals
				if(baseSize[1] === true && !filterInitialSizes(size, 'base-sizes')){
					conceptData.sizes.push({ width: parseInt(size.split('x')[0]), height: parseInt(size.split('x')[1]) });
				}
			});

			Object.values(values?.['custom-sizes']).forEach((size) => {
				const { width, height } = size;
				// remove existing custom sizes

				if(!filterInitialSizes(size, 'custom-sizes')) {
					conceptData.sizes.push({
						width: parseInt(width),
						height: parseInt(height),
					});
				}
			});
		}

		return conceptData;
	};

	return (
		<div className="select-ad creative-type">
			<header className="header-info">
				{renderHeader()}
			</header>
			<form className="checkbox-container" id={formName} onSubmit={makeConcept}>
				<div className="checkbox-col">
					<ReduxControls.Checkbox
						label="Standard Placement Sizes"
						name="standardPlacementSizes"
						checked={isAllSelected}
						onChange={handleSelectAll}
						disabled={selectAllDisabled}
						className="select-all-placement-size"
						 />
					{renderCheckboxes()}
				</div>
				<div className="checkbox-col custom">
					<CustomSizes
						formErrors={formErrors}
						customSizes={customSizes}
						errorMessages={errorMessages}
						initialValues={initialValues}
						isEdit={pristine}
						modalName={formName}
						 />
				</div>
			</form>
		</div>
	);
}

export function customValidate(values){
	let errors = {}
		, customSizes = values?.['custom-sizes']
		, customErrors = {};

	if(customSizes){
		let customSizesSet = new Set();

		customSizes.forEach((size, index) => {
			let { width, height } = size
				, formatted = `${width?.trim()}x${height?.trim()}`
				, customError = {}
				;

			if(width && height && customSizesSet.has(formatted) || adSizes.includes(formatted)){
				customError = {
					width: `Custom Placement Sizes - Placement sizes must be unique within the concept.`,
					height: `Custom Placement Sizes - Placement sizes must be unique within the concept.`,
				};
				customErrors[`custom-sizes[${index}]`] = customError;
			} else {
				customSizesSet.add(formatted);
			}
		});	//end customSizes.forEach()
	}	//end if(customSizes)

	if(Object.keys(customErrors).length){
		errors = {
			...errors,
			customErrors,
		};
	}	//end if(customErrorList.length)

	return errors;
}
